import { usePaginatedQuery } from '@hooks/usePaginatedQuery';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { GET_JOB_OFFER_UPERCUT, GET_JOB_OFFERS_PRO } from '@gql/jobs';

/**
 * Enhanced hook to fetch job offers based on current user's role
 * Automatically selects the appropriate query (Upercut vs Pro)
 * @param query - Search query string
 * @param filter - Filter options for job offers
 * @param sortBy - Sort field name
 * @param page - Current page (0-indexed)
 * @param pageSize - Number of items per page
 */
export const useGetJobOffers = (
    query: string = '',
    filter: any = {},
    sortBy: string = 'createdDate',
    page: number = 0,
    pageSize: number = 10,
) => {
    const { user, isUpercut } = useAuth();
    const { isProIdRequired } = useProId();

    const isUpercutView = isUpercut && !isProIdRequired;
    const queryToUse = isUpercutView ? GET_JOB_OFFER_UPERCUT : GET_JOB_OFFERS_PRO;

    const { rows, totalDocs, loading, isLoadingMore, hasMore, loadMore, error, refetch } = usePaginatedQuery({
        query: queryToUse,
        variables: { query, filter, sortBy },
        page,
        pageSize,
        baseLimit: 10,
        paginationMode: 'offset',
        skip: !user,
        extractResult: (data) => {
            const jobOffersData = isUpercutView ? data?.getJobOffersUpercut : data?.getJobOffersPro;
            return {
                docs: jobOffersData?.docs ?? [],
                meta: jobOffersData?.meta ?? { totalDocs: 0, page: 0, limit: pageSize, totalPages: 0, nextPage: null, prevPage: null },
                totalDocs: jobOffersData?.meta?.totalDocs ?? 0,
            };
        },
    });

    return {
        jobOffers: rows,
        totalDocs,
        totalPages: Math.ceil(totalDocs / pageSize),
        loading,
        isLoadingMore,
        hasMore,
        loadMore,
        error,
        refetch,
        isUpercut,
        userRole: user?.role,
    };
};
